import { useMemo, useState } from 'react'
import { Loader2, Plus } from 'lucide-react'

import { useAppStore } from '@core/store'
import type { ModeloStep } from '@viewer/lib/step/modeloStep'
import { lerArgTexto, lerListaDeRefs } from '@viewer/lib/step/textoStep'
import type { Operacao, Relatorio } from '@modules/ifc-edit/lib/operacoesEmMassa'
import { depoisDePintar, modeloEmTexto } from './modeloEmTexto'
import { agora, novoIdDeLote, simular } from './simular'
import RelatorioDeOperacoes from './RelatorioDeOperacoes'
import { AVISO, BTN, BTN_PRIMARIO, INPUT, NOTA, ROTULO } from './estilos'

type Accao = 'renomear-pset' | 'renomear-propriedade' | 'apagar-pset' | 'apagar-propriedade'

const ROTULOS: Record<Accao, string> = {
  'renomear-pset': 'Mudar o nome de um Pset',
  'renomear-propriedade': 'Mudar o nome de uma propriedade',
  'apagar-pset': 'Apagar um Pset',
  'apagar-propriedade': 'Apagar uma propriedade',
}

/** Nomes de conjuntos e das suas propriedades, e quantos há de cada. */
function nomes(m: ModeloStep): Map<string, { vezes: number; props: Set<string> }> {
  const out = new Map<string, { vezes: number; props: Set<string> }>()
  for (const classe of ['IFCPROPERTYSET', 'IFCELEMENTQUANTITY']) {
    for (const c of m.idsDaClasse(classe)) {
      const a = m.args(c)
      const n = lerArgTexto(a[2]) ?? ''
      const e = out.get(n) ?? { vezes: 0, props: new Set<string>() }
      e.vezes++
      for (const p of lerListaDeRefs(a[classe === 'IFCELEMENTQUANTITY' ? 5 : 4])) {
        const pn = lerArgTexto(m.args(p)[0])
        if (pn) e.props.add(pn)
      }
      out.set(n, e)
    }
  }
  return out
}

export default function SeparadorPsets({ modelId, aoJuntar }: { modelId: string; aoJuntar: () => void }) {
  const [accao, setAccao] = useState<Accao>('renomear-pset')
  const [pset, setPset] = useState('')
  const [prop, setProp] = useState('')
  const [novo, setNovo] = useState('')
  const [simulacao, setSimulacao] = useState<Relatorio | null>(null)
  const [aTrabalhar, setATrabalhar] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const acrescentarLote = useAppStore((s) => s.acrescentarLote)

  const catalogo = useMemo(() => {
    const m = modeloEmTexto(modelId)
    return m ? nomes(m) : null
  }, [modelId])

  const usaPropriedade = accao === 'renomear-propriedade' || accao === 'apagar-propriedade'
  const usaNovo = accao.startsWith('renomear')

  const operacao = (): Extract<Operacao, { tipo: 'renomear-pset' | 'renomear-propriedade' | 'apagar-pset' | 'apagar-propriedade' }> | null => {
    const p = pset.trim()
    const q = prop.trim()
    const n = novo.trim()
    if (!p || (usaPropriedade && !q) || (usaNovo && !n)) return null
    switch (accao) {
      case 'renomear-pset':
        return { tipo: 'renomear-pset', de: p, para: n }
      case 'renomear-propriedade':
        return { tipo: 'renomear-propriedade', pset: p, de: q, para: n }
      case 'apagar-pset':
        return { tipo: 'apagar-pset', nome: p }
      case 'apagar-propriedade':
        return { tipo: 'apagar-propriedade', pset: p, nome: q }
    }
  }

  const verificar = async () => {
    const op = operacao()
    if (!op) return
    setATrabalhar(true)
    setErro(null)
    await depoisDePintar()
    try {
      setSimulacao(simular(modelId, useAppStore.getState().ifcEditLotes, [op]))
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e))
    } finally {
      setATrabalhar(false)
    }
  }

  const juntar = () => {
    const op = operacao()
    if (!op) return
    const descricao =
      op.tipo === 'renomear-pset'
        ? `Renomear ${op.de} para ${op.para}`
        : op.tipo === 'renomear-propriedade'
          ? `Renomear ${op.pset}.${op.de} para ${op.para}`
          : op.tipo === 'apagar-pset'
            ? `Apagar ${op.nome}`
            : `Apagar ${op.pset}.${op.nome}`
    acrescentarLote({ id: novoIdDeLote(), descricao, quando: agora(), operacoes: [op] })
    useAppStore.getState().pushToast(`${descricao}: juntado às alterações pendentes.`, { type: 'success' })
    setSimulacao(null)
    setNovo('')
    aoJuntar()
  }

  if (!catalogo) {
    return <div className="p-3 text-[11px] text-[var(--color-text-muted)]">Este modelo não tem o texto do IFC em memória. Abre o .ifc.</div>
  }

  const props = catalogo.get(pset.trim())?.props
  const reservado = usaNovo && accao === 'renomear-pset' && /^(Pset|Qto)_/i.test(novo.trim()) && !/^(Pset|Qto)_/i.test(pset.trim())

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto p-3 text-[11px]">
      <datalist id="dados-psets">
        {[...catalogo.entries()].sort().map(([n, e]) => (
          <option key={n} value={n}>{`${e.vezes} conjunto(s)`}</option>
        ))}
      </datalist>
      <datalist id="dados-props">{props && [...props].sort().map((p) => <option key={p} value={p} />)}</datalist>
      <label className="flex flex-col gap-0.5">
        <span className={ROTULO}>Operação</span>
        <select className={INPUT} value={accao} onChange={(e) => { setAccao(e.target.value as Accao); setSimulacao(null) }}>
          {(Object.keys(ROTULOS) as Accao[]).map((a) => (
            <option key={a} value={a}>{ROTULOS[a]}</option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-0.5">
        <span className={ROTULO}>Pset ou Qto</span>
        <input className={INPUT} list="dados-psets" value={pset} onChange={(e) => { setPset(e.target.value); setSimulacao(null) }} placeholder="nome exacto, como está no ficheiro" />
      </label>
      {usaPropriedade && (
        <label className="flex flex-col gap-0.5">
          <span className={ROTULO}>Propriedade</span>
          <input className={INPUT} list="dados-props" value={prop} onChange={(e) => { setProp(e.target.value); setSimulacao(null) }} />
        </label>
      )}
      {usaNovo && (
        <label className="flex flex-col gap-0.5">
          <span className={ROTULO}>Nome novo</span>
          <input className={INPUT} value={novo} onChange={(e) => { setNovo(e.target.value); setSimulacao(null) }} />
        </label>
      )}
      {reservado && (
        <div className={AVISO}>Os prefixos Pset_ e Qto_ estão reservados aos conjuntos normalizados da buildingSMART.</div>
      )}
      <p className={NOTA}>
        Muda ou apaga todos os conjuntos com esse nome, em todas as classes e também nos tipos. Apagar leva as
        propriedades que mais ninguém usa e as relações que ficam vazias; nada fica a apontar para o que foi apagado.
      </p>
      <div className="flex gap-1">
        <button type="button" className={BTN} onClick={() => void verificar()} disabled={!operacao() || aTrabalhar}>
          {aTrabalhar && <Loader2 size={12} className="animate-spin" />} Simular
        </button>
        <button type="button" className={BTN_PRIMARIO} onClick={juntar} disabled={!simulacao || simulacao.aplicadas === 0}>
          <Plus size={12} /> Juntar às alterações
        </button>
      </div>
      {erro && <div className={AVISO}>{erro}</div>}
      {simulacao && <RelatorioDeOperacoes r={simulacao} />}
    </div>
  )
}
